import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function SignupPending() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [resendMessage, setResendMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const email = location.state?.email || new URLSearchParams(window.location.search).get('email') || '';

  // Check localStorage for cooldown on mount
  useEffect(() => {
    if (!email) return;

    const storageKey = `email_cooldown_${email}`;
    const lastSentTime = localStorage.getItem(storageKey);

    if (lastSentTime) {
      const now = Date.now();
      const lastSent = parseInt(lastSentTime, 10);
      const oneHourMs = 60 * 60 * 1000;
      const elapsed = now - lastSent;

      if (elapsed < oneHourMs) {
        const remaining = oneHourMs - elapsed;
        const seconds = Math.ceil(remaining / 1000);
        setCooldownSeconds(seconds);
        setResendDisabled(true);

        // Start countdown timer
        const interval = setInterval(() => {
          setCooldownSeconds(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              setResendDisabled(false);
              localStorage.removeItem(storageKey);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        return () => clearInterval(interval);
      } else {
        // Cooldown expired, clear it
        localStorage.removeItem(storageKey);
      }
    }
  }, [email]);

  const handleLogin = () => {
    logout();
    navigate('/login');
  };

  const handleResendConfirmation = async () => {
    if (!email) {
      setResendMessage('Email not found. Please sign up again.');
      setTimeout(() => setResendMessage(''), 3000);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/email/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.toLowerCase().trim(),
          resend: true,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendMessage('✓ Confirmation email sent! Check your inbox.');
        setResendDisabled(true);

        // Store the time in localStorage to persist across page refreshes
        const storageKey = `email_cooldown_${email}`;
        localStorage.setItem(storageKey, Date.now().toString());

        // Set 1-hour cooldown (3600 seconds)
        setCooldownSeconds(3600);

        // Start countdown timer
        const interval = setInterval(() => {
          setCooldownSeconds(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              setResendDisabled(false);
              localStorage.removeItem(storageKey);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        setTimeout(() => setResendMessage(''), 4000);
      } else {
        // Check if it's a rate limit error (429)
        if (response.status === 429) {
          setResendMessage(`⚠️ ${data.error}`);
        } else {
          setResendMessage(data.error || 'Failed to resend email');
        }
        setTimeout(() => setResendMessage(''), 4000);
      }
    } catch (err) {
      setResendMessage('Error: ' + err.message);
      setTimeout(() => setResendMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-margin-mobile pt-20">
      <div className="w-full max-w-md text-center">
        <div className="mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-success/10 mb-4">
            <span className="material-symbols-outlined text-success text-headline-lg">check_circle</span>
          </div>
          <h1 className="text-headline-lg text-on-background mb-3">Welcome to Wellis!</h1>
          <p className="text-body-md text-on-background/60">
            Your account has been created and approved. We've sent a verification email to <span className="font-medium text-on-background">{email}</span>
          </p>
        </div>

        {resendMessage && (
          <div className={`p-4 rounded-lg mb-6 text-body-sm ${
            resendMessage.includes('sent')
              ? 'bg-success/10 text-success border border-success/20'
              : 'bg-error/10 text-error border border-error/20'
          }`}>
            {resendMessage}
          </div>
        )}

        <div className="mb-8">
          <p className="text-body-sm text-on-background/60 mb-3">
            Didn't receive the email?{' '}
            <button
              onClick={handleResendConfirmation}
              disabled={loading || resendDisabled}
              className="text-primary font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading && 'Sending...'}
              {!loading && resendDisabled && `Resend in ${Math.floor(cooldownSeconds / 60)}m ${cooldownSeconds % 60}s`}
              {!loading && !resendDisabled && 'Resend confirmation'}
            </button>
          </p>
        </div>

        <div className="bg-primary-container/30 border border-primary/20 rounded-lg p-6 mb-8">
          <h2 className="text-title-md text-on-background mb-4 font-medium">What's next?</h2>
          <div className="text-left space-y-4">
            <div>
              <h3 className="text-body-sm font-medium text-on-background mb-2">Immediately available:</h3>
              <ul className="text-body-sm text-on-background/70 space-y-1 ml-4 list-disc">
                <li>View marketing plan</li>
                <li>Access welcome resources</li>
              </ul>
            </div>
            <div>
              <h3 className="text-body-sm font-medium text-on-background mb-2">After email verification:</h3>
              <ul className="text-body-sm text-on-background/70 space-y-1 ml-4 list-disc">
                <li>View your dashboard</li>
                <li>Access genealogy tree (yours and downlines)</li>
                <li>Invite new members</li>
                <li>Full access to marketing plan</li>
              </ul>
            </div>
            <div>
              <h3 className="text-body-sm font-medium text-on-background mb-2">After purchase verification:</h3>
              <ul className="text-body-sm text-on-background/70 space-y-1 ml-4 list-disc">
                <li>Unlock advanced features</li>
                <li>Complete genealogy placement</li>
              </ul>
            </div>
          </div>
        </div>

        <button
          onClick={handleLogin}
          className="w-full bg-primary text-on-primary font-medium py-2 rounded-lg hover:opacity-90 transition-opacity text-body-md"
        >
          Go to Login
        </button>
      </div>
    </div>
  );
}
